import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import {
  MailOutline,
  Build,
  Dashboard,
  Groups
} from '@mui/icons-material';
import {
  getOrganizationSchema,
  getSoftwareApplicationSchema
} from '../utils/seoSchemas';
import './Landing.css';

const Landing = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const [platformTab, setPlatformTab] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [activeHotspot, setActiveHotspot] = useState(null);

  useEffect(() => {
    if (!autoRotate) {
      return undefined;
    }
    const interval = setInterval(() => {
      setPlatformTab((prev) => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRotate]);

  // JSON-LD Structured Data for GEO
  const organizationSchema = getOrganizationSchema();
  const softwareAppSchema = getSoftwareApplicationSchema();

  if (isAuthenticated) {
    if (user?.role === 'technician') {
      return <Navigate to="/job-board" replace />;
    }
    return <Navigate to="/booking" replace />;
  }

  const infographicHotspots = [
    {
      id: 'booking',
      x: 12,
      y: 54,
      icon: 'email',
      title: 'AI Booking Intake',
      description:
        'Auto-classify and extract bookings from email. AI reads customer messages and creates structured job requests.',
      tooltipPosition: 'right'
    },
    {
      id: 'jobboard',
      x: 28,
      y: 36,
      icon: 'board',
      title: 'Job Board Flow',
      description:
        'Drag-and-drop job assignments with technician load tracking. Visual scheduling shows who is available.',
      tooltipPosition: 'right'
    },
    {
      id: 'healthcheck',
      x: 48,
      y: 54,
      icon: 'inspection',
      title: 'Vehicle Health Checks',
      description:
        'Digital inspections with media uploads and parts recommendations. Technicians capture issues on-site.',
      tooltipPosition: 'top'
    },
    {
      id: 'approval',
      x: 68,
      y: 36,
      icon: 'payment',
      title: 'Customer-Controlled Payments',
      description:
        'Customers approve only what matters. Safety-critical items are clearly highlighted for faster decisions.',
      tooltipPosition: 'left'
    },
    {
      id: 'invoice',
      x: 68,
      y: 68,
      icon: 'invoice',
      title: 'Invoices & Reporting',
      description:
        'Generate invoices automatically with VAT, parts, and labour split. Compliance fields stay consistent.',
      tooltipPosition: 'left'
    },
    {
      id: 'dashboard',
      x: 86,
      y: 54,
      icon: 'dashboard',
      title: 'Ops Dashboard',
      description:
        'Job status, pending payments, and completion timelines in one view. Stay ahead of customer questions.',
      tooltipPosition: 'left'
    }
  ];

  const infographicIcons = {
    email: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 6l-10 7L2 6" />
      </svg>
    ),
    board: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18" />
        <path d="M9 3v18" />
      </svg>
    ),
    inspection: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
        <path d="M11 8v6" />
        <path d="M8 11h6" />
      </svg>
    ),
    payment: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <path d="M1 10h22" />
        <path d="M6 16h4" />
      </svg>
    ),
    invoice: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M8 13h8" />
        <path d="M8 17h8" />
      </svg>
    ),
    dashboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    )
  };

  return (
    <Box className="landing-root">
      <Helmet>
        <title>Go Ops | AI Garage Management</title>
        <meta
          name="description"
          content="Go Ops is an AI-powered garage management system that turns booking emails into jobs, tracks work, and speeds approvals."
        />
        <link rel="canonical" href="https://www.go-ops.digital/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Go Ops | AI Garage Management" />
        <meta
          property="og:description"
          content="Go Ops is an AI-powered garage management system that turns booking emails into jobs, tracks work, and speeds approvals."
        />
        <meta property="og:url" content="https://www.go-ops.digital/" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Go Ops | AI Garage Management" />
        <meta
          name="twitter:description"
          content="Go Ops is an AI-powered garage management system that turns booking emails into jobs, tracks work, and speeds approvals."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(softwareAppSchema)}
        </script>
      </Helmet>
      <Box className="landing-topbar">
        <Container maxWidth="lg" className="landing-container">
          Boost performance & scale with Go Ops AI. Join the pilot now!
        </Container>
      </Box>
      <Box className="landing-nav">
        <Container maxWidth="lg" className="landing-container">
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 2 }}>
            <Typography className="landing-brand" variant="h6">
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" className="landing-cta-outline" component={RouterLink} to="/about">
                About
              </Button>
              <Button variant="text" className="landing-cta-outline" component={RouterLink} to="/faq">
                FAQ
              </Button>
              <Button variant="text" className="landing-cta-outline" component={RouterLink} to="/contact">
                Contact
              </Button>
              <Button variant="contained" className="landing-cta" component={RouterLink} to="/auth/login">
                Login
              </Button>
              <Button variant="outlined" className="landing-cta-outline" component={RouterLink} to="/contact">
                Sign Up
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="lg" className="landing-container">
        <Box className="landing-hero">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} className="landing-hero-copy">
              <Box className="landing-anim">
                <Typography component="h1" className="landing-title">
                  Deploy and run garages on an AI‑powered operations platform.
                </Typography>
                <Typography component="p" className="landing-subtitle">
                  Go Ops is an AI garage platform that converts booking emails into workflows,
                  coordinates technicians, and speeds up approvals — without the chaos.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button
                  className="landing-cta"
                  variant="contained"
                  component={RouterLink}
                  to="/auth/login"
                >
                  Get started now
                </Button>
                <Button
                  className="landing-cta-outline"
                  variant="outlined"
                  component={RouterLink}
                  to="/contact"
                >
                  Explore Go Ops
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="landing-card landing-anim delay-2">
                <span className="landing-metric-badge">Platform flow</span>
                <Typography variant="h6" className="landing-brand">
                  Deploy • Build • Manage • Scale
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Switch between platform views to see how Go Ops supports every stage of garage operations.
                </Typography>
                <div className="landing-divider" />
                <div className="landing-tabs">
                  <div className="landing-tabs-header">
                    <Tabs
                      value={platformTab}
                      onChange={(e, value) => setPlatformTab(value)}
                      variant="fullWidth"
                      textColor="primary"
                      indicatorColor="primary"
                    >
                      <Tab label="Deploy" className={platformTab === 0 ? 'landing-tab-active' : ''} />
                      <Tab label="Build" className={platformTab === 1 ? 'landing-tab-active' : ''} />
                      <Tab label="Manage" className={platformTab === 2 ? 'landing-tab-active' : ''} />
                      <Tab label="Scale" className={platformTab === 3 ? 'landing-tab-active' : ''} />
                    </Tabs>
                  </div>
                  <div className="landing-tabs-panel">
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      className={`landing-tabs-panel-inner ${platformTab === 0 ? 'active' : ''}`}
                      aria-hidden={platformTab !== 0}
                    >
                      <Grid item xs={12} md={6}>
                        <span className="landing-icon-pill">
                          <MailOutline fontSize="small" className="landing-icon" /> Intake
                        </span>
                        <Typography variant="subtitle1" className="landing-brand">
                          Deploy in minutes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Connect inboxes and booking sources, auto‑create jobs, and route them
                          into your live board with no manual data entry.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className="landing-tabs-graphic landing-tabs-graphic-deploy">
                          <Typography variant="subtitle2">Email → Booking</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Instant AI extraction, normalized customer and vehicle records.
                          </Typography>
                          <div className="landing-graphic-grid">
                            <div className="landing-graphic-tile"><strong>Inbox</strong></div>
                            <div className="landing-graphic-tile"><strong>AI Parse</strong></div>
                            <div className="landing-graphic-tile"><strong>Job Card</strong></div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      className={`landing-tabs-panel-inner ${platformTab === 1 ? 'active' : ''}`}
                      aria-hidden={platformTab !== 1}
                    >
                      <Grid item xs={12} md={6}>
                        <span className="landing-icon-pill">
                          <Build fontSize="small" className="landing-icon" /> Workflow
                        </span>
                        <Typography variant="subtitle1" className="landing-brand">
                          Build reliable workflows
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Job requests, parts approvals, and health checks connect into a single
                          workflow your team can trust.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className="landing-tabs-graphic landing-tabs-graphic-build">
                          <Typography variant="subtitle2">Tech → Admin → Customer</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Structured approvals with safety priority and pricing clarity.
                          </Typography>
                          <div className="landing-graphic-grid">
                            <div className="landing-graphic-tile"><strong>VHC</strong></div>
                            <div className="landing-graphic-tile"><strong>Parts</strong></div>
                            <div className="landing-graphic-tile"><strong>Payment</strong></div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      className={`landing-tabs-panel-inner ${platformTab === 2 ? 'active' : ''}`}
                      aria-hidden={platformTab !== 2}
                    >
                      <Grid item xs={12} md={6}>
                        <span className="landing-icon-pill">
                          <Dashboard fontSize="small" className="landing-icon" /> Visibility
                        </span>
                        <Typography variant="subtitle1" className="landing-brand">
                          Manage with visibility
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Live boards, technician capacity, and payment status in one place.
                          Stay ahead of delays and customer questions.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className="landing-tabs-graphic landing-tabs-graphic-manage">
                          <Typography variant="subtitle2">Ops Dashboard</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Job status, pending payments, and completion timelines.
                          </Typography>
                          <div className="landing-graphic-grid">
                            <div className="landing-graphic-tile"><strong>Board</strong></div>
                            <div className="landing-graphic-tile"><strong>Alerts</strong></div>
                            <div className="landing-graphic-tile"><strong>Payments</strong></div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      className={`landing-tabs-panel-inner ${platformTab === 3 ? 'active' : ''}`}
                      aria-hidden={platformTab !== 3}
                    >
                      <Grid item xs={12} md={6}>
                        <span className="landing-icon-pill">
                          <Groups fontSize="small" className="landing-icon" /> Multi‑tenant
                        </span>
                        <Typography variant="subtitle1" className="landing-brand">
                          Scale across garages
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Multi‑tenant ready with org‑specific branding, reporting, and invoice
                          settings. Grow without rebuilding tools.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className="landing-tabs-graphic landing-tabs-graphic-scale">
                          <Typography variant="subtitle2">Multi‑location ready</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Shared platform, isolated data, consistent workflows.
                          </Typography>
                          <div className="landing-graphic-grid">
                            <div className="landing-graphic-tile"><strong>Garage A</strong></div>
                            <div className="landing-graphic-tile"><strong>Garage B</strong></div>
                            <div className="landing-graphic-tile"><strong>Garage C</strong></div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </div>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className="landing-section">
          <Typography className="landing-section-title">Go Ops workflow at a glance</Typography>
          <Typography className="landing-muted" sx={{ mb: 3 }}>
            Hover each hotspot to explore how Go Ops takes a booking from inbox to completed work.
          </Typography>
          <div className="landing-infographic2">
            <div className="landing-infographic2-grid" />
            <svg className="landing-infographic2-lines" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <marker
                  id="landing-infographic2-arrow"
                  markerWidth="3"
                  markerHeight="3"
                  refX="3"
                  refY="1.5"
                  orient="auto"
                  markerUnits="userSpaceOnUse"
                >
                  <path d="M0,0 L0,3 L3,1.5 z" fill="rgba(59, 130, 246, 0.6)" />
                </marker>
              </defs>
              <path d="M 14 52 Q 20 44 26 38" markerEnd="url(#landing-infographic2-arrow)" />
              <path d="M 30 38 Q 40 46 46 52" markerEnd="url(#landing-infographic2-arrow)" />
              <path d="M 50 50 Q 60 42 66 38" markerEnd="url(#landing-infographic2-arrow)" />
              <path d="M 50 56 Q 60 64 66 68" markerEnd="url(#landing-infographic2-arrow)" />
              <path d="M 70 38 Q 80 46 84 52" markerEnd="url(#landing-infographic2-arrow)" />
              <path d="M 70 68 Q 80 60 84 56" markerEnd="url(#landing-infographic2-arrow)" />
            </svg>
            <div className="landing-infographic2-platform" />
            {infographicHotspots.map((hotspot) => {
              const isActive = activeHotspot === hotspot.id;
              return (
                <button
                  type="button"
                  key={hotspot.id}
                  className={`landing-infographic2-hotspot ${isActive ? 'active' : ''}`}
                  style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                  onMouseEnter={() => setActiveHotspot(hotspot.id)}
                  onMouseLeave={() => setActiveHotspot(null)}
                  onFocus={() => setActiveHotspot(hotspot.id)}
                  onBlur={() => setActiveHotspot(null)}
                  aria-label={hotspot.title}
                >
                  <span className="landing-infographic2-icon">
                    {infographicIcons[hotspot.icon]}
                  </span>
                  <span
                    className={`landing-infographic2-tooltip ${hotspot.tooltipPosition} ${isActive ? 'show' : ''}`}
                  >
                    <span className="landing-infographic2-tooltip-title">{hotspot.title}</span>
                    <span className="landing-infographic2-tooltip-text">{hotspot.description}</span>
                  </span>
                  {isActive && <span className="landing-infographic2-pulse" />}
                </button>
              );
            })}
            <div className="landing-infographic2-legend">
              <span>Intake</span>
              <span>→</span>
              <span>Assign</span>
              <span>→</span>
              <span>Inspect</span>
              <span>→</span>
              <span>Approve</span>
              <span>→</span>
              <span>Complete</span>
            </div>
          </div>
        </Box>

        <Box className="landing-section">
          <Typography className="landing-section-title">The Go Ops Platform</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Go Ops is an AI PaaS for garages — managed workflows, automated communication,
            and operational clarity without infrastructure headaches.
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                title: 'AI Booking Intake',
                desc: 'Auto‑classify and extract bookings from email, reducing manual work.'
              },
              {
                title: 'Job Board Flow',
                desc: 'Drag‑and‑drop job assignments with technician load tracking.'
              },
              {
                title: 'Vehicle Health Checks',
                desc: 'Digital inspections with media uploads and parts recommendations.'
              },
              {
                title: 'Customer‑Controlled Payments',
                desc: 'Approve only what matters — safety‑critical items clearly highlighted.'
              },
              {
                title: 'Invoices & Reporting',
                desc: 'Generate invoices automatically with VAT, parts, and labour split.'
              },
              {
                title: 'Multi‑tenant Ready',
                desc: 'Run multiple garages with isolated data and shared operational insights.'
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Box className={`landing-feature landing-anim delay-${index % 3}`}>
                  <Typography variant="subtitle1" className="landing-brand">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className="landing-section">
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography className="landing-section-title">Operational Experience (OpEx)</Typography>
              <Typography className="landing-muted">
                Built‑in monitoring for bookings, payments, and technician throughput. Stay ahead of
                delays with live dashboards and customer‑ready updates.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box className="landing-feature">
                    <Typography variant="subtitle2">Real‑time Job Board</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Visual scheduling, drag‑and‑drop assignment, and workload clarity.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="landing-feature">
                    <Typography variant="subtitle2">Automated Comms</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Send approvals, payment links, and updates without manual follow‑ups.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="landing-feature">
                    <Typography variant="subtitle2">Payments that Convert</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Customers choose what to approve. Safety‑critical items stand out.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className="landing-feature">
                    <Typography variant="subtitle2">Invoice + VAT Ready</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generate PDFs with full compliance fields and clean line‑item splits.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box className="landing-section landing-card">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography className="landing-section-title">Security & Compliance</Typography>
              <Typography className="landing-muted">
                Your customer data and payments are protected with secure authentication,
                controlled access, and structured audit trails.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {['Role‑based access', 'Stripe‑secured payments', 'Audit trail & activity log', 'Multi‑tenant isolation']
                  .map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <Box className="landing-feature">
                        <Typography variant="subtitle2">{item}</Typography>
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Box className="landing-section">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography className="landing-section-title">Cost savings & business impact</Typography>
              <Typography className="landing-muted">
                Reduce booking admin, shorten approval cycles, and reclaim technician time.
                Go Ops is built to lift margins without adding headcount.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button className="landing-cta" variant="contained" component={RouterLink} to="/auth/login">
                  Get started now
                </Button>
                <Button className="landing-cta-outline" variant="outlined" component={RouterLink} to="/contact">
                  Book a demo
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box className="landing-card">
                <Typography variant="overline" className="landing-muted">
                  Indicative impact
                </Typography>
                <Typography variant="h4" className="landing-brand">+35%</Typography>
                <Typography className="landing-muted" variant="body2">
                  Reduction in admin time spent on booking triage and approvals.
                </Typography>
                <div className="landing-divider" />
                <Typography variant="h4" className="landing-brand">+22%</Typography>
                <Typography className="landing-muted" variant="body2">
                  Faster payment approvals through selective pay flows.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className="landing-section" sx={{ pb: 6 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Go Ops. AI‑based garage management system.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Landing;
